/* Bounded-concurrency runner for multi-version execution.
 *
 * Running every selected version at once means one Web Worker + one freshly
 * instantiated wasm module (~15–22 MiB each) per version simultaneously — heavy
 * enough to jank the tab and exhaust memory past a handful. This caps how many
 * tasks execute concurrently; the rest queue and start as slots free up.
 *
 * Generic over the item/result type and order-preserving: results[i] always
 * corresponds to items[i] regardless of completion order. A task that rejects
 * propagates its rejection (the caller decides per-item error handling inside
 * the task), so one failure never silently drops a slot. */

/** Default pool size — real WASM is heavy; 3–4 keeps the tab responsive. */
export const DEFAULT_POOL_SIZE = 4;

/**
 * Run `task` over `items` with at most `limit` in flight at once.
 * @returns results aligned to `items` order.
 */
export async function runPool<T, R>(
  items: T[],
  task: (item: T, index: number) => Promise<R>,
  limit: number = DEFAULT_POOL_SIZE,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;

  // Each worker pulls the next index until the queue drains. `limit` workers
  // run concurrently; clamp to the item count so we never spawn idle pullers.
  const pull = async (): Promise<void> => {
    while (next < items.length) {
      const i = next++;
      results[i] = await task(items[i], i);
    }
  };

  const workers = Array.from({ length: Math.min(limit, items.length) }, pull);
  await Promise.all(workers);
  return results;
}
